import { create } from "zustand";
import firebase from "@services/firebase";
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

interface AuthStore {
  user: User | null;
  loading: boolean;
  initAuthListener: () => (() => void) | void;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  loading: true,

  initAuthListener: () => {
    const unsubscribe = onAuthStateChanged(firebase.auth, (u) => {
      set({ user: u, loading: false });
    });

    // Return unsubscribe function for cleanup
    return unsubscribe;
  },

  login: async (email, password, rememberMe = false) => {
    try {
      // Set persistence BEFORE signing in
      await setPersistence(
        firebase.auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence,
      );

      // Now sign in
      await signInWithEmailAndPassword(firebase.auth, email, password);

      // User will be set automatically by onAuthStateChanged listener
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  },

  loginWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(firebase.auth, provider);
      // onAuthStateChanged fires automatically; App.tsx calls fetchUserData
    } catch (error) {
      console.error("Google login error:", error);
      throw error;
    }
  },

  logout: async () => {
    try {
      await signOut(firebase.auth);
      set({ user: null });
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  },
}));
